'use client';

import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from 'react';
import type { Game } from '@/lib/games';
import { COVER_FRAMES } from '@/lib/gameCovers';
import { GAME_VIDEOS } from '@/lib/gameVideos';
import GameCover from './GameCover';
import GameTutorialDialog from './GameTutorialDialog';
import { fmtMB, localVideoUrl, useGameEnable, type EnableState } from '@/lib/videoCache';

/**
 * A game's cover as the game itself: real frames, playing while you look.
 *
 * The frames are screenshots taken by `scripts/shoot-game-covers.mjs` — the
 * title screen, then two moments of play — cropped to where the game draws.
 * At rest the tile shows the first.
 *
 * While `live` (the tile is hovered or focused):
 *   · a game with a TUTORIAL VIDEO (lib/gameVideos.ts, recorded by
 *     scripts/record-game-video.mjs from the game's scripted attract run) plays
 *     it muted, inline and looping from the start, with a thin progress bar;
 *     it pauses and rewinds when the tile goes quiet. Nothing is downloaded
 *     until a tile first goes live, so a shelf of forty-odd tiles costs nothing.
 *   · every other game crossfades through its frames under a slow push-in.
 *
 * Phones have no hover (a tap opens the game), so there the one video tile
 * nearest the middle of the screen plays by itself while it is in view.
 *
 * A tile-sized cover of a game with a video also carries a "Watch tutorial"
 * button: it opens the tutorial BIG (GameTutorialDialog) instead of launching
 * the game, even though it sits inside the tile's play button.
 *
 * A game with no frames (new, or not shot yet) keeps the drawn cover, and so
 * does one whose image fails to load — a broken-image icon on a shelf is worse
 * than either picture. A video that fails falls back to the frames.
 */
export default function GameShot({
  game,
  live = false,
  className = '',
  gate = true,
}: {
  game: Game;
  live?: boolean;
  className?: string;
  /**
   * "Watch video" (lib/videoCache): a video game stays grey with a Watch
   * video label until its tutorial is stored in this browser, then plays the
   * local copy. Off for the console, which always streams.
   */
  gate?: boolean;
}) {
  const frames = COVER_FRAMES[game.id] ?? 0;
  const [at, setAt] = useState(0);
  const [broken, setBroken] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const en = useGameEnable(game.id, gate);
  const gated = gate && !!GAME_VIDEOS[game.id] && en.s !== 'on';
  const hasVideo = !!GAME_VIDEOS[game.id] && !videoBroken && !gated;
  const boxRef = useRef<HTMLSpanElement>(null);
  const autoLive = useAutoLive(boxRef, hasVideo);
  const [watch, setWatch] = useState(false);
  const big = useWide(boxRef, !!GAME_VIDEOS[game.id], 150);   // the chip only on a real tile, not a list thumbnail
  const on = (live || autoLive) && !watch;
  // "Video ready ✓" for a moment right after the download finishes (not on a page load that finds it stored).
  const [flash, setFlash] = useState(false);
  const was = useRef(en.s);
  useEffect(() => {
    const before = was.current; was.current = en.s;
    if (before === 'loading' && en.s === 'on') { setFlash(true); const t = setTimeout(() => setFlash(false), 1600); return () => clearTimeout(t); }
  }, [en.s]);
  // The stored copy, opened from disk the first time this tile goes live.
  const [local, setLocal] = useState<string | null>(null);
  useEffect(() => {
    if (!gate || en.s !== 'on') return;
    if (en.url) { setLocal(en.url); return; }
    if (on) void localVideoUrl(game.id).then((u) => { if (u) setLocal(u); });
  }, [gate, en, on, game.id]);

  useEffect(() => {
    if (hasVideo || !on || frames < 2) { setAt(0); return; }
    // Straight to the second frame, so the hover answers at once instead of
    // after a full beat of nothing happening.
    setAt(1);
    const t = setInterval(() => setAt((i) => (i + 1) % frames), 1100);
    return () => clearInterval(t);
  }, [on, frames, hasVideo]);

  if ((!frames && !hasVideo && !gated) || broken) return <GameCover game={game} className={className} />;

  return (
    <span ref={boxRef} className={'relative block overflow-hidden bg-black ' + className}>
      {Array.from({ length: hasVideo ? Math.min(1, frames) : frames }, (_, i) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={i}
          src={`/games/covers/${game.id}-${i + 1}.webp`}
          alt={i === 0 ? game.name : ''}
          aria-hidden={i !== 0}
          draggable={false}
          // Lazy: forty-odd covers below the fold of the shelf can wait.
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className={
            'absolute inset-0 h-full w-full object-cover transition-[opacity,transform] ease-out ' +
            (on && !hasVideo && !gated ? 'duration-[1400ms] scale-[1.08]' : 'duration-500 scale-100') +
            (gated ? ' grayscale brightness-[.6]' : '')
          }
          style={{ opacity: i === at ? 1 : 0 }}
        />
      ))}
      {hasVideo && (!gate || local) && <ShotVideo id={game.id} src={gate ? local : null} live={on} onFail={() => setVideoBroken(true)} />}
      {hasVideo && big && <WatchChip onOpen={() => setWatch(true)} />}
      {gated && <EnableGate game={game} en={en} big={big} />}
      {flash && (
        <span className="pointer-events-none absolute inset-x-0 top-2 z-[3] flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white shadow-xl">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12l5 5 9-10" /></svg>
            {big ? 'Video ready' : 'Ready'}
          </span>
        </span>
      )}
      {watch && (
        <GameTutorialDialog
          game={game}
          video={GAME_VIDEOS[game.id]}
          playLabel={playLabelOf(boxRef.current, game)}
          onPlay={tileButton(boxRef.current) ? () => tileButton(boxRef.current)?.click() : undefined}
          onClose={() => setWatch(false)}
        />
      )}
      {/* A glass edge and a floor, so a photo of a dark game still reads as a
          lit screen sitting in the card rather than a hole in it. */}
      <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-white/5" />
      {on && !hasVideo && !gated && frames > 1 && (
        <span aria-hidden className="pointer-events-none absolute bottom-1.5 left-1/2 flex -translate-x-1/2 gap-1">
          {Array.from({ length: frames }, (_, i) => (
            <span key={i} className={
              'h-1 rounded-full transition-all duration-300 ' +
              (i === at ? 'w-3 bg-white' : 'w-1 bg-white/50')
            } />
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * "Watch video": the grey tile's call to action and its loading progress.
 * It is only a picture — the click belongs to the tile's own button, whose
 * pick() (GamesMenu) loads the tutorial into this browser (lib/videoCache)
 * and then opens the purchase dialog / the game. Always shown on a big tile:
 * watching the video is the first step for anyone who has not seen it.
 */
function EnableGate({ game, en, big }: { game: Game; en: EnableState; big: boolean }) {
  if (en.s === 'unknown') return null;

  if (en.s === 'loading') {
    const pct = en.total ? Math.min(100, Math.floor((en.loaded / en.total) * 100)) : 0;
    const R = 22, C = 2 * Math.PI * R;
    return (
      <span
        role="status"
        aria-label={`Loading ${game.name} video: ${pct}%`}
        className="pointer-events-none absolute inset-0 z-[3] flex flex-col items-center justify-center gap-1.5 bg-black/55 text-white backdrop-blur-[1px]"
      >
        <span className="relative grid h-14 w-14 place-items-center">
          <svg viewBox="0 0 56 56" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
            <circle cx="28" cy="28" r={R} fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="5" />
            <circle cx="28" cy="28" r={R} fill="none" stroke="url(#gt-en-grad)" strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset .15s linear' }} />
            <defs><linearGradient id="gt-en-grad" x1="0" x2="1"><stop offset="0" stopColor="#fbbf24" /><stop offset=".5" stopColor="#f97316" /><stop offset="1" stopColor="#dc2626" /></linearGradient></defs>
          </svg>
          <span className="text-[13px] font-black tabular-nums">{pct}%</span>
        </span>
        <span className="text-[11px] font-extrabold uppercase tracking-[0.18em]">Loading video</span>
        {big && <span className="text-[10px] tabular-nums text-white/60">{fmtMB(en.loaded)}{en.total ? ` / ${fmtMB(en.total)}` : ''}</span>}
        <span className="absolute inset-x-0 bottom-0 h-1 bg-white/15">
          <span className="block h-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-600" style={{ width: `${pct}%`, transition: 'width .15s linear' }} />
        </span>
      </span>
    );
  }

  if (!big) return null;   // list thumbnails stay grey; the big tile carries the label
  const failed = en.s === 'error';
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 z-[3] grid place-items-center">
      <span
        className="inline-flex flex-col items-center gap-0.5 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 px-5 py-2.5
                   text-white shadow-2xl ring-2 ring-white/20 transition group-hover:scale-105"
      >
        <span className="inline-flex items-center gap-1.5 text-[13px] font-black uppercase tracking-wider">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden><path d="M7 4.5v15l12.5-7.5z" /></svg>
          {failed ? 'Try again' : 'Watch video'}
        </span>
        <span className="text-[10px] font-semibold text-white/85">{failed ? 'The video did not load — tap to retry' : 'See how it plays first'}</span>
      </span>
    </span>
  );
}

/** The tile's own button (the cover sits inside it) — "Play" in the tutorial presses it. */
function tileButton(el: HTMLElement | null) { return el?.closest('button') ?? null; }
function playLabelOf(el: HTMLElement | null, game: Game) {
  const b = tileButton(el); if (!b) return undefined;
  const a = b.getAttribute('aria-label') || '';
  return /^buy\b/i.test(a) ? `Buy ${game.name}` : `Play ${game.name}`;
}

/**
 * "Watch tutorial". It lives inside the tile's play <button>, so it is a span
 * with a button role that swallows its own click / Enter / Space — opening the
 * tutorial must never also launch the game.
 */
function WatchChip({ onOpen }: { onOpen: () => void }) {
  const stop = (e: MouseEvent | KeyboardEvent) => { e.stopPropagation(); e.preventDefault(); };
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label="Watch tutorial"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { stop(e); onOpen(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { stop(e); onOpen(); } }}
      onKeyUp={(e) => { if (e.key === ' ') stop(e); }}
      className="absolute bottom-2 right-2 z-[2] inline-flex min-h-[32px] cursor-pointer items-center gap-1.5 rounded-full bg-black/70 py-1 pl-1.5 pr-3
                 text-[11px] font-extrabold uppercase tracking-wider text-white shadow-lg ring-1 ring-orange-400/70 backdrop-blur-sm transition
                 hover:bg-black/85 hover:ring-orange-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
    >
      <span aria-hidden className="grid h-5 w-5 place-items-center rounded-full bg-gradient-to-br from-amber-300 via-orange-500 to-red-600">
        <svg viewBox="0 0 24 24" className="ml-px h-2.5 w-2.5" fill="currentColor"><path d="M7 4.5v15l12.5-7.5z" /></svg>
      </span>
      Watch tutorial
    </span>
  );
}

/** Whether the element is at least `min` px wide (kept up to date as it resizes). */
function useWide(ref: RefObject<HTMLElement | null>, enabled: boolean, min: number) {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const check = () => setWide(el.getBoundingClientRect().width >= min);
    check();
    const ro = new ResizeObserver(check); ro.observe(el);
    return () => ro.disconnect();
  }, [ref, enabled, min]);
  return wide;
}

/**
 * The tutorial video. Mounted only once its tile has first gone live (so a
 * resting shelf downloads no video at all), then kept: going quiet pauses it
 * and rewinds to the start, and it fades out over the still cover.
 */
function ShotVideo({ id, src, live, onFail }: { id: string; src: string | null; live: boolean; onFail: () => void }) {
  const [armed, setArmed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  const bar = useRef<HTMLSpanElement>(null);

  useEffect(() => { if (live) setArmed(true); }, [live]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (live) {
      v.muted = true;   // the attribute alone is not enough for some autoplay policies
      try { v.currentTime = 0; } catch { /* not seekable yet: it starts at 0 anyway */ }
      const p = v.play();
      if (p) p.catch(() => { /* autoplay refused (power saving): the still cover stays */ });
      // The progress bar, read off the video every frame while it plays.
      let raf = 0;
      const tick = () => {
        if (bar.current && v.duration) bar.current.style.transform = `scaleX(${Math.min(1, v.currentTime / v.duration)})`;
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }
    v.pause();
    try { v.currentTime = 0; } catch { /* fine */ }
    setPlaying(false);
    if (bar.current) bar.current.style.transform = 'scaleX(0)';
  }, [live, armed]);

  if (!armed) return null;
  const show = live && playing;
  return (
    <>
      <video
        ref={ref}
        src={src ?? undefined}
        muted
        playsInline
        loop
        autoPlay
        preload="auto"
        poster={`/games/videos/${id}.jpg`}
        aria-hidden
        disablePictureInPicture
        onPlaying={() => setPlaying(true)}
        onError={onFail}
        className={'absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ' + (show ? 'opacity-100' : 'opacity-0')}
      >
        {/* The browser's own stored copy when the game is enabled; else streamed. */}
        {!src && <source src={`/games/videos/${id}.webm`} type="video/webm" onError={() => { /* falls through to the mp4 */ }} />}
        {!src && <source src={`/games/videos/${id}.mp4`} type="video/mp4" onError={onFail} />}
      </video>
      <span aria-hidden className={'pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[3px] bg-white/15 transition-opacity duration-300 ' + (show ? 'opacity-100' : 'opacity-0')}>
        <span ref={bar} className="block h-full origin-left bg-amber-400" style={{ transform: 'scaleX(0)' }} />
      </span>
    </>
  );
}

/*
 * Touch screens: one video tile at a time plays on its own — the one nearest
 * the middle of the screen, while at least 60 % of it is in view. Desktop
 * (any hover-capable pointer) never auto-plays; hover decides there.
 */
type AutoEntry = { el: HTMLElement; set: (on: boolean) => void };
const AUTO = new Set<AutoEntry>();
let autoRaf = 0;
function pickAuto() {
  autoRaf = 0;
  const vh = window.innerHeight, mid = vh / 2;
  let best: AutoEntry | null = null, bd = Infinity;
  for (const e of Array.from(AUTO)) {
    const r = e.el.getBoundingClientRect();
    if (!r.height) continue;
    const seen = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0)) / r.height;
    const d = Math.abs((r.top + r.bottom) / 2 - mid);
    if (seen >= 0.6 && d < bd) { bd = d; best = e; }
  }
  AUTO.forEach((e) => e.set(e === best));
}
function queueAuto() { if (!autoRaf) autoRaf = requestAnimationFrame(pickAuto); }

function useAutoLive(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof window === 'undefined' || !window.matchMedia('(hover: none)').matches) return;
    const entry: AutoEntry = { el, set: setOn };
    AUTO.add(entry);
    if (AUTO.size === 1) {
      window.addEventListener('scroll', queueAuto, { passive: true, capture: true });
      window.addEventListener('resize', queueAuto);
    }
    queueAuto();
    return () => {
      AUTO.delete(entry);
      setOn(false);
      if (!AUTO.size) {
        window.removeEventListener('scroll', queueAuto, { capture: true });
        window.removeEventListener('resize', queueAuto);
      }
      queueAuto();
    };
  }, [ref, enabled]);
  return on;
}
